/**
 * The translation function, in two forms:
 *
 *  - `translate(key, params?)` — standalone, reads the current language from the store. For code
 *    that runs OUTSIDE the React render tree (outbox toasts, mutation-hook `toast.*` calls that may
 *    fire after unmount). It does not subscribe; it just resolves against `getLang()` at call time.
 *  - `useTranslation()` (in `useTranslation.ts`) wraps this and subscribes, so components re-render
 *    on a language switch.
 *
 * Lookup falls back to English if a key is somehow absent at runtime — the compiler already
 * guarantees full coverage (`messages/index.ts`), so this only guards against a value that is not a
 * `MessageKey` sneaking in through an `as` cast. It NEVER renders a raw key back to the user.
 *
 * Interpolation is `{name}` placeholders: `translate('x.greeting', { name: 'Ada' })`.
 */
import { getLang, type Language } from './store';
import { en, messages, type MessageKey } from './messages';

export type TParams = Record<string, string | number>;

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(template: string, params?: TParams): string {
  if (params === undefined) return template;
  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/** Resolve a key against an explicit language. Used by both the standalone and the hook. */
export function translateIn(lang: Language, key: MessageKey, params?: TParams): string {
  const template = messages[lang][key] ?? en[key];
  return interpolate(template, params);
}

/** Standalone translate, bound to the live language. Safe to call from non-React code. */
export function translate(key: MessageKey, params?: TParams): string {
  return translateIn(getLang(), key, params);
}

/**
 * Cardinal pluralization. English and German share the same rule (1 → `.one`, everything else →
 * `.other`), so a two-form convention is all we need — no CLDR plural machinery. Pass a `base` key
 * whose `.one` and `.other` variants both exist in the catalog. `count` is auto-provided as the
 * `{count}` param.
 */
export function translatePlural(
  lang: Language,
  base: string,
  count: number,
  params?: TParams,
): string {
  const suffix = count === 1 ? '.one' : '.other';
  return translateIn(lang, (base + suffix) as MessageKey, { count, ...params });
}
